import {
  DashboardMap,
  type DashboardMapInfoCard,
  type DashboardMapPin,
} from "@/components/dashboard/dashboard-map";
import { DashboardMapViewLayout } from "@/components/dashboard/dashboard-map-view-layout";
import { DashboardViewToggle } from "@/components/dashboard/dashboard-view-toggle";
import { MapErrorBoundary } from "@/components/dashboard/map-error-boundary";
import { DashboardColumnFilterBadges } from "@/components/non_registrant/dashboard-column-filter-badges";
import {
  buildColumnFilterBadges,
  removeColumnFilterBadge,
} from "@/components/non_registrant/dashboard-column-filter-utils";
import { DashboardMapFacetRail } from "@/components/non_registrant/dashboard-map-facet-rail";
import {
  type ColumnProperties,
  DashboardTable,
  type ExportColumnMode,
  dashboardTableLayoutBaseKey,
  exportTableRowsToCsv,
  exportTableRowsToExcel,
  getExportAccessorKeysForMode,
} from "@/components/non_registrant/dashboard-table";
import { StrLinkedLicenseSheet } from "@/components/non_registrant/str-linked-license-sheet";
import {
  StrPropertyDetailsSheet,
  safeHttpUrl,
  scopedStrLicenseId,
} from "@/components/non_registrant/str-property-details";
import { Button } from "@/components/ui/button";
import ErrorComponent from "@/components/ui/error";
import LoadingScreen from "@/components/ui/loading-screen";
import { UserType } from "@/constants";
import {
  type StrCatalogListing,
  type StrCatalogMapPin,
  type StrMappedAddress,
  type StrPropertyCatalog,
  type StrUnitCategory,
  getAllStrListings,
  getAllStrProperties,
  getStrListingFilterValues,
  getStrListingMapPins,
  getStrListings,
  getStrProperties,
  getStrPropertyDetail,
  getStrPropertyFilterValues,
  getStrPropertyMapPins,
  setStrListingUnitCategory,
  setStrPropertyUnitCategory,
  suggestStrUnitCategories,
} from "@/hera-api/str-address-mapping-api";
import { useListColumnFilterSync } from "@/hooks/use-sync-global-column-filters";
import { useSyncSharedTextColumns } from "@/hooks/use-sync-shared-text-columns";
import { dashboardListQueryOptions } from "@/lib/dashboard-query-options";
import {
  LICENSE_SHARED_TEXT_COLUMNS,
  STR_SHARED_TEXT_COLUMNS,
} from "@/lib/shared-map-list-column-filters";
import type { NamedIdOption } from "@/lib/sync-global-column-filters";
import {
  formatDateNoTimezone,
  getQueryErrorHeading,
  getQueryErrorMessage,
} from "@/lib/utils";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useLocation, useNavigate } from "@tanstack/react-router";
import type { OnChangeFn, SortingState } from "@tanstack/react-table";
import { Copy, type LucideIcon, MapPinOff, UserRound } from "lucide-react";
import {
  type ComponentProps,
  type Dispatch,
  type ReactNode,
  type SetStateAction,
  useCallback,
  useMemo,
  useRef,
  useState,
} from "react";

export const STR_CATALOG_PAGE_SIZES = [10, 25, 50, 100] as const;

export type StrCatalogPageSize = (typeof STR_CATALOG_PAGE_SIZES)[number];

export interface StrCatalogSearch {
  view: "list" | "map";
  page: number;
  pageSize: StrCatalogPageSize;
  sort: string;
  direction: "asc" | "desc";
  filters: string;
}

export const LISTING_ACCESSOR_KEYS = [
  "platform",
  "title",
  "first_seen",
  "listing_id",
  "rooms",
  "city",
  "location",
  "property_group",
  "mapped_address",
  "mapping",
  "property_type",
  "jurisdiction",
  "regions",
  "flags",
] as const;

export const PROPERTY_ACCESSOR_KEYS = [
  "title",
  "first_seen",
  "platforms",
  "city",
  "rooms",
  "location",
  "listing_count",
  "related_properties",
  "mapped_address",
  "mapping",
  "property_type",
  "jurisdiction",
  "regions",
] as const;

export function catalogAccessorKeys<T extends string>(
  keys: readonly T[],
  showRegions: boolean,
): T[] {
  if (showRegions) return [...keys];
  return keys.filter((key) => key !== "regions");
}

export const LISTING_FLAG_UNMAPPED = "Address Not Mapped";
export const LISTING_FLAG_UNMARKED_DUPLICATE =
  "Multiple listings at same address not marked as duplicate";
export const LISTING_FLAG_NEARBY_OWNER = "Nearby owner matches host";

const LISTING_FLAG_MARKS: Record<
  string,
  { icon: LucideIcon; className: string }
> = {
  [LISTING_FLAG_UNMAPPED]: {
    icon: MapPinOff,
    className: "bg-amber-100 text-amber-900",
  },
  [LISTING_FLAG_UNMARKED_DUPLICATE]: {
    icon: Copy,
    className: "bg-rose-100 text-rose-900",
  },
  [LISTING_FLAG_NEARBY_OWNER]: {
    icon: UserRound,
    className: "bg-sky-100 text-sky-900",
  },
};

export const STR_MAPPED_PIN_COLOR = "#3b82f6";
export const STR_EXACT_UNVERIFIED_PIN_COLOR = "#f97316";
export const STR_UNMAPPED_PIN_COLOR = "#eab308";
export const STR_PROPERTY_PIN_LEGEND = [
  { color: STR_MAPPED_PIN_COLOR, label: "Address mapped and licensed" },
  { color: "#ef4444", label: "Address mapped, no license" },
  {
    color: STR_EXACT_UNVERIFIED_PIN_COLOR,
    label: "Exact location, address not verified",
  },
  { color: STR_UNMAPPED_PIN_COLOR, label: "No address" },
] as const;

export const UNIT_CATEGORY_OPTIONS: {
  value: StrUnitCategory;
  label: string;
}[] = [
  { value: "entire_unit", label: "Entire unit" },
  { value: "one_of_many", label: "One unit of many" },
  { value: "adu", label: "ADU" },
  { value: "shared_space", label: "Shared space" },
  { value: "rv", label: "RV" },
  { value: "tent", label: "Tent" },
  { value: "campsite", label: "Campsite" },
];

type ListingAccessor = (typeof LISTING_ACCESSOR_KEYS)[number];
type PropertyAccessor = (typeof PROPERTY_ACCESSOR_KEYS)[number];

const SORT_ALIASES: Record<string, string> = {
  address: "mapped_address",
  listings: "listing_count",
};

function blankLabel(value: string | null | undefined): string {
  const text = (value ?? "").trim();
  return text || "(Empty)";
}

function wordLabel(value: string | null | undefined): string {
  const text = (value ?? "").trim();
  if (!text) return "(Empty)";
  return text
    .split(" ")
    .map((part) => (part ? part[0].toUpperCase() + part.slice(1) : part))
    .join(" ");
}

function numberText(value: number | null | undefined): string {
  if (value == null || Number.isNaN(value)) return "—";
  return Number.isInteger(value) ? String(value) : String(value);
}

export function locationLabel(status: string): string {
  if (status === "exact") return "Exact";
  if (status === "approximate") return "Approximate";
  return "No location";
}

export function listingRoomsLabel(
  bedrooms: number | null | undefined,
  bathrooms: number | null | undefined,
): string {
  if (bedrooms == null && bathrooms == null) return "(Empty)";
  return `${numberText(bedrooms)} bd · ${numberText(bathrooms)} ba`;
}

export function propertyRoomsLabel(
  low: number | null | undefined,
  high: number | null | undefined,
): string {
  if (low == null) return "(Empty)";
  return high == null || low === high
    ? `${numberText(low)} bd`
    : `${numberText(low)}–${numberText(high)} bd`;
}

function countLabel(count: number, singular: string, plural: string): string {
  return `${count} ${count === 1 ? singular : plural}`;
}

export function listingAddressLabel(
  address: Pick<StrMappedAddress, "formatted_address"> | null,
): string {
  return address?.formatted_address?.trim() ?? "";
}

export function listingMappingLabel(
  address: Pick<StrMappedAddress, "formatted_address"> | null,
): string {
  return address ? "Mapped" : "Not mapped";
}

export function strCatalogPinColor(mapped: boolean): string {
  return mapped ? STR_MAPPED_PIN_COLOR : STR_UNMAPPED_PIN_COLOR;
}

export const STR_NON_COMPLIANT_PIN_COLOR = "#ef4444";

export function strPropertyPinColor(input: {
  mapped: boolean;
  licensed?: boolean;
  locationExact?: boolean;
  compliance?: "compliant" | "non_compliant" | "unmapped" | null;
}): string {
  if (input.mapped) {
    const licensed = input.licensed ?? input.compliance === "compliant";
    return licensed ? STR_MAPPED_PIN_COLOR : STR_NON_COMPLIANT_PIN_COLOR;
  }
  if (input.locationExact) return STR_EXACT_UNVERIFIED_PIN_COLOR;
  return STR_UNMAPPED_PIN_COLOR;
}

export function unitCategoryLabel(
  category: StrUnitCategory | null | undefined,
  mixed = false,
): string {
  if (mixed) return "Mixed";
  return (
    UNIT_CATEGORY_OPTIONS.find((option) => option.value === category)?.label ??
    "(Empty)"
  );
}

export function propertyAddressLabel(row: {
  address_status: StrPropertyCatalog["address_status"];
  address: Pick<StrMappedAddress, "formatted_address"> | null;
}): string {
  if (row.address_status === "conflict") return "Address conflict";
  const formatted = row.address?.formatted_address?.trim() ?? "";
  if (row.address_status === "building_only") {
    return formatted ? `${formatted} · Building only` : "Building only";
  }
  return formatted;
}

export function propertyMappingLabel(
  status: StrPropertyCatalog["address_status"],
): string {
  if (status === "conflict") return "Address conflict";
  if (status === "unmapped") return "Not mapped";
  return "Mapped";
}

export function jurisdictionLabel(value: string | null | undefined): string {
  return (value ?? "").trim();
}

export function listingFlagLabel(flags: readonly string[] | undefined): string {
  return (flags ?? []).join(", ");
}

export function ListingFlagMarks({
  flags,
}: {
  flags?: readonly string[];
}) {
  const marks = flags ?? [];
  if (marks.length === 0) return null;
  return (
    <span className="inline-flex items-center gap-1">
      {marks.map((flag) => {
        const appearance = LISTING_FLAG_MARKS[flag];
        const Icon = appearance?.icon ?? MapPinOff;
        return (
          <span
            key={flag}
            title={flag}
            aria-label={flag}
            className={`inline-flex h-5 w-5 items-center justify-center rounded-full ${appearance?.className ?? "bg-muted text-foreground"}`}
          >
            <Icon className="h-3 w-3" aria-hidden />
          </span>
        );
      })}
    </span>
  );
}

function platformsLabel(platforms: string[]): string {
  const label = platforms.map((platform) => wordLabel(platform)).join(", ");
  return label || "(Empty)";
}

export function parseStrCatalogFilters(raw: string): Record<string, string[]> {
  if (!raw.trim()) return {};
  try {
    const data = JSON.parse(raw) as unknown;
    if (!data || typeof data !== "object" || Array.isArray(data)) return {};
    const filters: Record<string, string[]> = {};
    for (const [key, value] of Object.entries(data)) {
      if (!Array.isArray(value)) continue;
      const values = value.filter(
        (item): item is string => typeof item === "string" && item.length > 0,
      );
      if (values.length > 0) filters[key] = values;
    }
    return filters;
  } catch {
    return {};
  }
}

export function serializeStrCatalogFilters(
  filters: Record<string, string[]>,
): string {
  const ordered: Record<string, string[]> = {};
  for (const key of Object.keys(filters).sort()) {
    const values = filters[key];
    if (values?.length) ordered[key] = values;
  }
  return Object.keys(ordered).length === 0 ? "" : JSON.stringify(ordered);
}

export function validateStrCatalogSearch(
  search: Record<string, unknown>,
  defaultSort: string,
  allowedSorts: readonly string[],
): StrCatalogSearch {
  const rawSort = typeof search.sort === "string" ? search.sort : "";
  const resolved = SORT_ALIASES[rawSort] ?? rawSort;
  const allowed = new Set(allowedSorts);
  const pageSizeNumber = Number(search.pageSize ?? 50);
  const pageSize = (STR_CATALOG_PAGE_SIZES as readonly number[]).includes(
    pageSizeNumber,
  )
    ? (pageSizeNumber as StrCatalogPageSize)
    : 50;
  const pageNumber = Number(search.page ?? 1);
  const page =
    Number.isFinite(pageNumber) && pageNumber >= 1 ? Math.floor(pageNumber) : 1;
  return {
    view: search.view === "map" ? "map" : "list",
    page,
    pageSize,
    sort: allowed.has(resolved) ? resolved : defaultSort,
    direction: search.direction === "desc" ? "desc" : "asc",
    filters: typeof search.filters === "string" ? search.filters : "",
  };
}

export function defaultStrListingsSearch(): StrCatalogSearch {
  return validateStrCatalogSearch({}, "platform", LISTING_ACCESSOR_KEYS);
}

export function strCatalogReturnTo(
  pathname: string,
  search: StrCatalogSearch,
): string {
  const params = new URLSearchParams();
  params.set("view", search.view);
  params.set("page", String(search.page));
  params.set("pageSize", String(search.pageSize));
  params.set("sort", search.sort);
  params.set("direction", search.direction);
  if (search.filters) params.set("filters", search.filters);
  return `${pathname}?${params.toString()}`;
}

function persistedColumnVisibility(
  tablePersistenceKey: string,
): Record<string, boolean> {
  if (typeof window === "undefined") return {};
  try {
    const raw = window.localStorage.getItem(
      `${dashboardTableLayoutBaseKey(tablePersistenceKey)}:visibility`,
    );
    if (!raw) return {};
    const parsed = JSON.parse(raw) as unknown;
    if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
      return {};
    }
    const visibility: Record<string, boolean> = {};
    for (const [key, value] of Object.entries(parsed)) {
      if (typeof value === "boolean") visibility[key] = value;
    }
    return visibility;
  } catch {
    return {};
  }
}

export function getListingColumnProperties(
  accessorKey: ListingAccessor,
): ColumnProperties<StrCatalogListing, ListingAccessor> {
  switch (accessorKey) {
    case "platform":
      return {
        displayName: "Platform",
        defaultVisible: true,
        getValue: (row) => wordLabel(row.platform),
      };
    case "title":
      return {
        displayName: "Title",
        defaultVisible: true,
        getValue: (row) => blankLabel(row.title),
      };
    case "first_seen":
      return {
        displayName: "First seen",
        defaultVisible: true,
        getValue: (row) =>
          row.first_seen_on
            ? formatDateNoTimezone(row.first_seen_on)
            : "(Empty)",
      };
    case "listing_id":
      return {
        displayName: "Listing ID",
        defaultVisible: true,
        getValue: (row) => blankLabel(row.listing_id),
      };
    case "rooms":
      return {
        displayName: "Rooms",
        defaultVisible: true,
        getValue: (row) => listingRoomsLabel(row.bedrooms, row.bathrooms),
      };
    case "city":
      return {
        displayName: "City",
        defaultVisible: true,
        getValue: (row) => wordLabel(row.city),
      };
    case "location":
      return {
        displayName: "Location",
        defaultVisible: true,
        allowBlankFilter: false,
        getValue: (row) => locationLabel(row.location_status),
      };
    case "property_group":
      return {
        displayName: "Property",
        defaultVisible: true,
        allowBlankFilter: false,
        getValue: (row) =>
          countLabel(row.property_listing_count, "listing", "listings"),
      };
    case "mapped_address":
      return {
        displayName: "Mapped address",
        defaultVisible: true,
        getValue: (row) => listingAddressLabel(row.address),
      };
    case "mapping":
      return {
        displayName: "Mapping",
        defaultVisible: true,
        allowBlankFilter: false,
        getValue: (row) => listingMappingLabel(row.address),
      };
    case "property_type":
      return {
        displayName: "Property type",
        defaultVisible: true,
        getValue: (row) => unitCategoryLabel(row.unit_category),
      };
    case "jurisdiction":
      return {
        displayName: "Jurisdiction",
        defaultVisible: true,
        getValue: (row) => jurisdictionLabel(row.jurisdiction),
      };
    case "regions":
      return {
        displayName: "Regions",
        defaultVisible: true,
        getValue: (row) => blankLabel(row.regions),
      };
    case "flags":
      return {
        displayName: "Flags",
        defaultVisible: true,
        getValue: (row) => listingFlagLabel(row.flags),
        renderCell: (row) => <ListingFlagMarks flags={row.flags} />,
      };
  }
}

export function getPropertyColumnProperties(
  accessorKey: PropertyAccessor,
): ColumnProperties<StrPropertyCatalog, PropertyAccessor> {
  switch (accessorKey) {
    case "title":
      return {
        displayName: "Title",
        defaultVisible: true,
        getValue: (row) => blankLabel(row.title),
      };
    case "first_seen":
      return {
        displayName: "First seen",
        defaultVisible: true,
        getValue: (row) =>
          row.first_seen_on
            ? formatDateNoTimezone(row.first_seen_on)
            : "(Empty)",
      };
    case "platforms":
      return {
        displayName: "Platforms",
        defaultVisible: true,
        getValue: (row) => platformsLabel(row.platforms),
      };
    case "city":
      return {
        displayName: "City",
        defaultVisible: true,
        getValue: (row) => wordLabel(row.city),
      };
    case "rooms":
      return {
        displayName: "Rooms",
        defaultVisible: true,
        getValue: (row) =>
          propertyRoomsLabel(row.bedrooms_min, row.bedrooms_max),
      };
    case "location":
      return {
        displayName: "Location",
        defaultVisible: true,
        allowBlankFilter: false,
        getValue: (row) => locationLabel(row.location_status),
      };
    case "listing_count":
      return {
        displayName: "Listings",
        defaultVisible: true,
        allowBlankFilter: false,
        getValue: (row) => countLabel(row.listing_count, "listing", "listings"),
      };
    case "related_properties":
      return {
        displayName: "Related",
        defaultVisible: true,
        allowBlankFilter: false,
        getValue: (row) =>
          countLabel(row.related_property_count, "related", "related"),
      };
    case "mapped_address":
      return {
        displayName: "Mapped address",
        defaultVisible: true,
        getValue: (row) => propertyAddressLabel(row),
        renderCell: (row) => {
          if (row.address_status !== "conflict")
            return propertyAddressLabel(row);
          return (
            <div>
              <p className="font-medium text-destructive">Address conflict</p>
              {row.conflicting_addresses.map((address) => (
                <p key={address} className="text-xs font-normal">
                  {address}
                </p>
              ))}
            </div>
          );
        },
      };
    case "mapping":
      return {
        displayName: "Mapping",
        defaultVisible: true,
        allowBlankFilter: false,
        getValue: (row) => propertyMappingLabel(row.address_status),
      };
    case "property_type":
      return {
        displayName: "Property type",
        defaultVisible: true,
        getValue: (row) =>
          unitCategoryLabel(row.unit_category, row.unit_category_mixed),
      };
    case "jurisdiction":
      return {
        displayName: "Jurisdiction",
        defaultVisible: true,
        getValue: (row) => jurisdictionLabel(row.jurisdiction),
      };
    case "regions":
      return {
        displayName: "Regions",
        defaultVisible: true,
        getValue: (row) => blankLabel(row.regions),
      };
  }
}

function withFacetValues<T, K extends string>(
  base: ColumnProperties<T, K>,
  values: string[] | undefined,
): ColumnProperties<T, K> {
  if (!values) return base;
  return { ...base, filterValues: values };
}

function UnitCategorySelect({
  label,
  category,
  source,
  mixed = false,
  disabled,
  onChange,
}: {
  label: string;
  category: StrUnitCategory | null | undefined;
  source: "ai" | "human" | null | undefined;
  mixed?: boolean;
  disabled?: boolean;
  onChange: (category: StrUnitCategory | null) => void;
}) {
  return (
    <div
      className="flex items-center gap-2"
      onClick={(event) => event.stopPropagation()}
      onKeyDown={(event) => event.stopPropagation()}
    >
      <select
        aria-label={label}
        className="h-8 max-w-44 rounded-md border bg-background px-2 text-xs"
        value={mixed ? "" : (category ?? "")}
        disabled={disabled}
        onChange={(event) => {
          event.stopPropagation();
          const next = event.target.value;
          if (!next) {
            if (!category && !mixed) return;
            onChange(null);
            return;
          }
          if (next === category && !mixed) return;
          onChange(next as StrUnitCategory);
        }}
      >
        <option value="">
          {mixed ? "Mixed" : source === "human" ? "Use suggestion" : "Not set"}
        </option>
        {UNIT_CATEGORY_OPTIONS.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
      {source === "ai" && !mixed ? (
        <span className="text-xs text-muted-foreground">Suggested</span>
      ) : null}
    </div>
  );
}

function finitePins(pins: StrCatalogMapPin[]) {
  return pins.filter(
    (pin) => Number.isFinite(pin.lat) && Number.isFinite(pin.lng),
  );
}

export function listingMapPins(pins: StrCatalogMapPin[]): DashboardMapPin[] {
  return finitePins(pins).map((pin) => ({
    id: pin.id,
    lat: pin.lat,
    lng: pin.lng,
    address: pin.address,
    display_id: pin.display_id,
    status: "COMPLIANT_UNTIL_RENEWAL_DATE" as const,
    renewal_date: null,
    color: strPropertyMapPinColor(pin),
  }));
}

export function strPropertyMapPinColor(pin: {
  pin_color?: string | null;
  mapped?: boolean;
  address_unverified?: boolean;
  status_label?: string | null;
}): string {
  if (pin.pin_color) return pin.pin_color;
  const label = pin.status_label?.trim() ?? "";
  return strPropertyPinColor({
    mapped: pin.mapped === true,
    licensed: label.length > 0 && label !== "Unregistered",
    locationExact: pin.address_unverified === true,
  });
}

export function propertyMapPins(pins: StrCatalogMapPin[]): DashboardMapPin[] {
  return finitePins(pins).map((pin) => {
    return {
      id: pin.id,
      lat: pin.lat,
      lng: pin.lng,
      address: pin.address,
      display_id: pin.display_id,
      status: "COMPLIANT_UNTIL_RENEWAL_DATE" as const,
      renewal_date: null,
      color: strPropertyMapPinColor(pin),
      strCard: {
        statusLabel: pin.status_label?.trim() || "Unregistered",
        platforms: pin.platforms ?? [],
        heroImageUrl: safeHttpUrl(pin.hero_image_url),
        addressUnverified: pin.address_unverified === true,
      },
    };
  });
}

export type StrCatalogSharedFilters = {
  columnFilters: Record<string, string[]>;
  setColumnFilters: Dispatch<SetStateAction<Record<string, string[]>>>;
  selectedJurisdictions?: string[];
  setSelectedJurisdictions?: (ids: string[]) => void;
  jurisdictions?: readonly NamedIdOption[];
  selectedRegions?: string[];
  setSelectedRegions?: (ids: string[]) => void;
  regions?: readonly NamedIdOption[];
};

export function StrCatalog({
  kind,
  search,
  onSearchChange,
  propertyScope,
  scopeControls,
  regionOverlay,
  sharedFilters,
}: {
  kind: "listings" | "properties";
  search: StrCatalogSearch;
  onSearchChange: (next: StrCatalogSearch) => void;
  propertyScope?: {
    jurisdictionIds: string[];
    regionIds: string[];
    includeUnassignedRegions: boolean;
  };
  scopeControls?: ReactNode;
  regionOverlay?: {
    selectedJurisdictionIds: readonly string[];
    portalJurisdictionIds: readonly string[];
    onRegionFilterChange: (regionIds: string[]) => void;
  };
  /** License column filters and header selections this catalog should follow. */
  sharedFilters?: StrCatalogSharedFilters;
}) {
  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const filters = useMemo(
    () => parseStrCatalogFilters(search.filters),
    [search.filters],
  );
  const filtersRef = useRef(filters);
  const seenUrlFilters = useRef(search.filters);
  if (seenUrlFilters.current !== search.filters) {
    seenUrlFilters.current = search.filters;
    filtersRef.current = filters;
  }
  const searchRef = useRef(search);
  searchRef.current = search;
  const setSharedCatalogFilters = useCallback(
    (action: SetStateAction<Record<string, string[]>>) => {
      const current = filtersRef.current;
      const next = typeof action === "function" ? action(current) : action;
      const serialized = serializeStrCatalogFilters(next);
      filtersRef.current = next;
      if (serialized === searchRef.current.filters) return;
      seenUrlFilters.current = serialized;
      onSearchChange({
        ...searchRef.current,
        page: 1,
        filters: serialized,
      });
    },
    [onSearchChange],
  );
  const sharedTextBuckets = useMemo(() => {
    const catalogBucket = {
      filters,
      setFilters: setSharedCatalogFilters,
      columns: STR_SHARED_TEXT_COLUMNS,
    };
    if (!sharedFilters) return [catalogBucket];
    return [
      {
        filters: sharedFilters.columnFilters,
        setFilters: sharedFilters.setColumnFilters,
        columns: LICENSE_SHARED_TEXT_COLUMNS,
      },
      catalogBucket,
    ];
  }, [filters, setSharedCatalogFilters, sharedFilters]);
  useSyncSharedTextColumns({ buckets: sharedTextBuckets });
  useListColumnFilterSync({
    columnFilters: filters,
    setColumnFilters: setSharedCatalogFilters,
    jurisdictionColumnId: sharedFilters?.setSelectedJurisdictions
      ? "jurisdiction"
      : undefined,
    regionColumnId: sharedFilters?.setSelectedRegions ? "regions" : undefined,
    selectedJurisdictions: sharedFilters?.selectedJurisdictions,
    setSelectedJurisdictions: sharedFilters?.setSelectedJurisdictions,
    jurisdictions: sharedFilters?.jurisdictions,
    selectedRegions: sharedFilters?.selectedRegions,
    setSelectedRegions: sharedFilters?.setSelectedRegions,
    regions: sharedFilters?.regions,
  });
  const effectiveFiltersSerialized = useMemo(
    () => serializeStrCatalogFilters(filters),
    [filters],
  );
  const returnTo = strCatalogReturnTo(location.pathname, search);
  const isListings = kind === "listings";
  const title = isListings ? "STR listings" : "STR properties";
  const description = isListings
    ? "One row for each active marketplace listing."
    : "One row for each property. Listings marked as the same, two units at the same address, or a portion of each other share a row.";
  const tablePersistenceKey = isListings
    ? "operator-str-listings"
    : "operator-str-properties";
  const [facetValues, setFacetValues] = useState<Record<string, string[]>>({});
  const [columnVisibility, setColumnVisibility] = useState<
    Record<string, boolean>
  >({});
  const filterRequestIds = useRef<Record<string, number>>({});

  const catalogQuery = {
    page: search.page,
    pageSize: search.pageSize,
    sort: search.sort,
    direction: search.direction,
    filters: effectiveFiltersSerialized || undefined,
    ...(!isListings ? propertyScope : {}),
  };
  const catalogQueryKey = [
    search.page,
    search.pageSize,
    search.sort,
    search.direction,
    effectiveFiltersSerialized,
    propertyScope,
  ] as const;
  const listingListQuery = useQuery({
    queryKey: ["str-catalog", "listings", ...catalogQueryKey],
    queryFn: () => getStrListings(catalogQuery),
    enabled: isListings,
    ...dashboardListQueryOptions,
  });
  const propertyListQuery = useQuery({
    queryKey: ["str-catalog", "properties", ...catalogQueryKey],
    queryFn: () => getStrProperties(catalogQuery),
    enabled: !isListings,
    ...dashboardListQueryOptions,
  });
  const listQuery = isListings ? listingListQuery : propertyListQuery;
  const pinsQuery = useQuery({
    queryKey: [
      "str-catalog-pins",
      kind,
      effectiveFiltersSerialized,
      propertyScope,
    ],
    queryFn: () =>
      isListings
        ? getStrListingMapPins({
            filters: effectiveFiltersSerialized || undefined,
          })
        : getStrPropertyMapPins({
            filters: effectiveFiltersSerialized || undefined,
            ...propertyScope,
          }),
    enabled: search.view === "map",
    ...dashboardListQueryOptions,
  });
  const [propertyDetailId, setPropertyDetailId] = useState<string | null>(null);
  const [propertyLicenseId, setPropertyLicenseId] = useState<string | null>(
    null,
  );
  const propertyDetailQuery = useQuery({
    queryKey: ["str-property-detail", propertyDetailId],
    queryFn: () => getStrPropertyDetail(propertyDetailId ?? ""),
    enabled: !isListings && propertyDetailId != null,
  });

  const saveUnitCategory = useMutation({
    mutationFn: async (input: {
      listingId?: string;
      representativeId?: string;
      category: StrUnitCategory | null;
    }) => {
      if (input.listingId) {
        await setStrListingUnitCategory(input.listingId, input.category);
        return;
      }
      if (input.representativeId) {
        await setStrPropertyUnitCategory(
          input.representativeId,
          input.category,
        );
      }
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["str-catalog"] });
      await queryClient.invalidateQueries({ queryKey: ["str-catalog-pins"] });
    },
  });
  const suggestUnitCategories = useMutation({
    mutationFn: () =>
      suggestStrUnitCategories({
        kind,
        filters: effectiveFiltersSerialized || undefined,
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["str-catalog"] });
      await queryClient.invalidateQueries({ queryKey: ["str-catalog-pins"] });
    },
  });
  const listingColumns = useCallback(
    (key: ListingAccessor) => {
      const column = withFacetValues(
        getListingColumnProperties(key),
        facetValues[key],
      );
      if (key !== "property_type") return column;
      return {
        ...column,
        renderCell: (row: StrCatalogListing) => (
          <UnitCategorySelect
            label={`Property type for ${row.title || row.listing_id}`}
            category={row.unit_category}
            source={row.unit_category_source}
            disabled={saveUnitCategory.isPending}
            onChange={(category) =>
              saveUnitCategory.mutate({ listingId: row.id, category })
            }
          />
        ),
      };
    },
    [facetValues, saveUnitCategory],
  );
  const propertyColumns = useCallback(
    (key: PropertyAccessor) => {
      const column = withFacetValues(
        getPropertyColumnProperties(key),
        facetValues[key],
      );
      if (key !== "property_type") return column;
      return {
        ...column,
        renderCell: (row: StrPropertyCatalog) => (
          <UnitCategorySelect
            label={`Property type for ${row.title || "property"}`}
            category={row.unit_category}
            source={row.unit_category_source}
            mixed={row.unit_category_mixed}
            disabled={saveUnitCategory.isPending}
            onChange={(category) =>
              saveUnitCategory.mutate({
                representativeId: row.representative_id,
                category,
              })
            }
          />
        ),
      };
    },
    [facetValues, saveUnitCategory],
  );

  const columnFilterBadges = useMemo(
    () =>
      isListings
        ? buildColumnFilterBadges(
            filters,
            LISTING_ACCESSOR_KEYS,
            getListingColumnProperties,
          )
        : buildColumnFilterBadges(
            filters,
            PROPERTY_ACCESSOR_KEYS,
            getPropertyColumnProperties,
          ),
    [filters, isListings],
  );

  const handleFiltersChange = setSharedCatalogFilters;
  const handleSortingChange: OnChangeFn<SortingState> = useCallback(
    (updater) => {
      const current: SortingState = [
        { id: search.sort, desc: search.direction === "desc" },
      ];
      const next = typeof updater === "function" ? updater(current) : updater;
      const sort = next[0];
      onSearchChange({
        ...search,
        page: 1,
        sort: sort?.id || (isListings ? "platform" : "title"),
        direction: sort?.desc ? "desc" : "asc",
      });
    },
    [isListings, onSearchChange, search],
  );
  const handleFilterPopoverOpen = useCallback(
    async (columnId: string, q?: string) => {
      const requestId = (filterRequestIds.current[columnId] ?? 0) + 1;
      filterRequestIds.current[columnId] = requestId;
      try {
        const values = isListings
          ? await getStrListingFilterValues({
              column: columnId,
              filters: effectiveFiltersSerialized || undefined,
              q,
            })
          : await getStrPropertyFilterValues({
              column: columnId,
              filters: effectiveFiltersSerialized || undefined,
              q,
              ...propertyScope,
            });
        if (filterRequestIds.current[columnId] !== requestId) return;
        setFacetValues((prev) => ({ ...prev, [columnId]: values }));
      } catch {
        if (filterRequestIds.current[columnId] !== requestId) return;
        setFacetValues((prev) => ({ ...prev, [columnId]: [] }));
      }
    },
    [effectiveFiltersSerialized, isListings, propertyScope],
  );
  const handleRefresh = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: ["str-catalog", kind] });
    void queryClient.invalidateQueries({
      queryKey: ["str-catalog-pins", kind],
    });
  }, [kind, queryClient]);
  const openMapping = useCallback(
    (id: string) => {
      void navigate({
        to: "/operator/str/address-mapping",
        search: {
          listingId: isListings ? id : undefined,
          propertyListingId: isListings ? undefined : id,
          returnTo,
        },
      });
    },
    [isListings, navigate, returnTo],
  );
  const openPropertyDetails = useCallback((id: string) => {
    setPropertyLicenseId(null);
    setPropertyDetailId(id);
  }, []);
  const handleServerExport = useCallback(
    async (mode: ExportColumnMode, format: "csv" | "excel") => {
      const query = {
        sort: search.sort,
        direction: search.direction,
        filters: effectiveFiltersSerialized || undefined,
        ...(!isListings ? propertyScope : {}),
      };
      const visibility =
        Object.keys(columnVisibility).length > 0
          ? columnVisibility
          : persistedColumnVisibility(tablePersistenceKey);
      if (isListings) {
        const rows = await getAllStrListings(query);
        const exportKeys = getExportAccessorKeysForMode(
          mode,
          null,
          LISTING_ACCESSOR_KEYS,
          undefined,
          visibility as Record<ListingAccessor, boolean>,
        );
        const props = (key: ListingAccessor) => getListingColumnProperties(key);
        const listingRows = rows;
        if (format === "csv") {
          exportTableRowsToCsv({
            rows: listingRows,
            exportKeys: exportKeys as ListingAccessor[],
            getColumnProperties: props,
            fileBasename: "str-listings",
          });
        } else {
          exportTableRowsToExcel({
            rows: listingRows,
            exportKeys: exportKeys as ListingAccessor[],
            getColumnProperties: props,
            fileBasename: "str-listings",
          });
        }
        return;
      }
      const rows = await getAllStrProperties(query);
      const exportKeys = getExportAccessorKeysForMode(
        mode,
        null,
        PROPERTY_ACCESSOR_KEYS,
        undefined,
        visibility as Record<PropertyAccessor, boolean>,
      );
      const props = (key: PropertyAccessor) => getPropertyColumnProperties(key);
      if (format === "csv") {
        exportTableRowsToCsv({
          rows,
          exportKeys: exportKeys as PropertyAccessor[],
          getColumnProperties: props,
          fileBasename: "str-properties",
        });
      } else {
        exportTableRowsToExcel({
          rows,
          exportKeys: exportKeys as PropertyAccessor[],
          getColumnProperties: props,
          fileBasename: "str-properties",
        });
      }
    },
    [
      columnVisibility,
      effectiveFiltersSerialized,
      isListings,
      propertyScope,
      search.direction,
      search.sort,
      tablePersistenceKey,
    ],
  );

  const pins = useMemo(
    () =>
      isListings
        ? listingMapPins(pinsQuery.data ?? [])
        : propertyMapPins(pinsQuery.data ?? []),
    [isListings, pinsQuery.data],
  );
  const chrome = (
    <div className="space-y-4 pb-2">
      <div>
        <h1 className="text-2xl font-semibold">{title}</h1>
        <p className="text-sm text-muted-foreground">{description}</p>
      </div>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <ul className="flex flex-wrap gap-3 text-sm" aria-label="Map legend">
          {STR_PROPERTY_PIN_LEGEND.map((item) => (
            <li key={item.label} className="flex items-center gap-1">
              <span
                className="inline-block h-3 w-3 rounded-full"
                style={{ backgroundColor: item.color }}
              />
              {item.label}
            </li>
          ))}
        </ul>
        <div className="flex flex-wrap items-center justify-end gap-2">
          {!isListings ? scopeControls : null}
          <Button
            type="button"
            variant="outline"
            disabled={suggestUnitCategories.isPending}
            onClick={() => suggestUnitCategories.mutate()}
          >
            {suggestUnitCategories.isPending
              ? "Suggesting property types…"
              : "Suggest property types"}
          </Button>
          <DashboardViewToggle
            isMapView={search.view === "map"}
            onViewChange={(isMapView) =>
              onSearchChange({ ...search, view: isMapView ? "map" : "list" })
            }
          />
        </div>
      </div>
      {suggestUnitCategories.isSuccess ? (
        <p className="text-sm text-muted-foreground">
          Suggested property types for{" "}
          {suggestUnitCategories.data.classified_count}{" "}
          {suggestUnitCategories.data.classified_count === 1
            ? "listing"
            : "listings"}
          .
        </p>
      ) : null}
      {suggestUnitCategories.isError || saveUnitCategory.isError ? (
        <p className="text-sm text-destructive" role="alert">
          {getQueryErrorMessage(
            suggestUnitCategories.error ?? saveUnitCategory.error,
          )}
        </p>
      ) : null}
    </div>
  );

  if (
    search.view === "list" &&
    listQuery.isLoading &&
    listQuery.data === undefined
  ) {
    return (
      <div className="flex min-h-0 flex-1 flex-col gap-4">
        {chrome}
        <LoadingScreen message={`Fetching ${title.toLowerCase()}...`} />
      </div>
    );
  }
  if (listQuery.isError && listQuery.data === undefined) {
    return (
      <div className="flex min-h-0 flex-1 flex-col gap-4">
        {chrome}
        <ErrorComponent
          title={getQueryErrorHeading(listQuery.error)}
          message={getQueryErrorMessage(listQuery.error)}
          action={{ label: "Retry", onClick: () => void listQuery.refetch() }}
        />
      </div>
    );
  }

  const sharedTableProps = {
    columnFilters: filters,
    onColumnFiltersChange: handleFiltersChange,
    columnVisibility,
    onColumnVisibilityChange: setColumnVisibility,
    tablePersistenceKey,
    serverMode: true as const,
    lockToViewport: true,
    rowCount: listQuery.data?.total ?? 0,
    pageIndex: search.page - 1,
    pageSize: search.pageSize,
    pageSizeOptions: STR_CATALOG_PAGE_SIZES,
    onPaginationChange: (next: { pageIndex: number; pageSize: number }) => {
      const pageSize = (STR_CATALOG_PAGE_SIZES as readonly number[]).includes(
        next.pageSize,
      )
        ? (next.pageSize as StrCatalogPageSize)
        : search.pageSize;
      onSearchChange({
        ...search,
        page: next.pageIndex + 1,
        pageSize,
      });
    },
    sorting: [
      { id: search.sort, desc: search.direction === "desc" },
    ] as SortingState,
    onSortingChange: handleSortingChange,
    onRefresh: handleRefresh,
    isRefreshing:
      listQuery.isFetching || (search.view === "map" && pinsQuery.isFetching),
    refreshAriaLabel: `Refresh ${title.toLowerCase()}`,
    dataUpdatedAt: listQuery.dataUpdatedAt,
    onFilterPopoverOpen: handleFilterPopoverOpen,
    onServerExport: handleServerExport,
    rowActionsColumnWidth: 180,
  };

  const frame = isListings ? (
    <StrCatalogFrame<StrCatalogListing, ListingAccessor>
      chrome={chrome}
      isMapView={search.view === "map"}
      onListView={() => onSearchChange({ ...search, view: "list" })}
      pins={pins}
      infoCard="registration"
      pinsLoading={pinsQuery.isFetching}
      pinsError={pinsQuery.error}
      onPinsRetry={() => void pinsQuery.refetch()}
      onPinClick={openMapping}
      columnFilterBadges={columnFilterBadges}
      filters={filters}
      onFiltersChange={handleFiltersChange}
      accessorKeys={catalogAccessorKeys(
        LISTING_ACCESSOR_KEYS,
        listQuery.data?.show_regions !== false,
      )}
      getColumnProperties={listingColumns}
      rows={(listQuery.data?.items ?? []) as StrCatalogListing[]}
      getRowId={(row) => row.id}
      onRowClick={openMapping}
      renderRowActions={(row) => (
        <CatalogRowLinks listingId={row.id} returnTo={returnTo} />
      )}
      {...sharedTableProps}
    />
  ) : (
    <StrCatalogFrame<StrPropertyCatalog, PropertyAccessor>
      chrome={chrome}
      isMapView={search.view === "map"}
      onListView={() => onSearchChange({ ...search, view: "list" })}
      pins={pins}
      infoCard="str-property"
      pinsLoading={pinsQuery.isFetching}
      pinsError={pinsQuery.error}
      onPinsRetry={() => void pinsQuery.refetch()}
      onPinClick={openPropertyDetails}
      regionOverlay={regionOverlay}
      columnFilterBadges={columnFilterBadges}
      filters={filters}
      onFiltersChange={handleFiltersChange}
      accessorKeys={catalogAccessorKeys(
        PROPERTY_ACCESSOR_KEYS,
        listQuery.data?.show_regions !== false,
      )}
      getColumnProperties={propertyColumns}
      rows={(listQuery.data?.items ?? []) as StrPropertyCatalog[]}
      getRowId={(row) => row.representative_id}
      onRowClick={openPropertyDetails}
      renderRowActions={(row) => (
        <CatalogRowLinks
          propertyListingId={row.representative_id}
          returnTo={returnTo}
        />
      )}
      {...sharedTableProps}
    />
  );
  const propertyDetail = propertyDetailQuery.data;

  return (
    <>
      {frame}
      {isListings ? null : (
        <>
          <StrPropertyDetailsSheet
            userType={UserType.OPERATOR}
            open={propertyDetailId != null}
            onOpenChange={(open) => {
              if (!open) {
                setPropertyDetailId(null);
                setPropertyLicenseId(null);
              }
            }}
            title={
              propertyDetail?.address || propertyDetail?.title || "Property"
            }
            loading={
              propertyDetailId != null &&
              propertyDetail == null &&
              !propertyDetailQuery.isError
            }
            errorMessage={
              propertyDetailQuery.isError
                ? getQueryErrorMessage(propertyDetailQuery.error)
                : null
            }
            detail={
              propertyDetail
                ? {
                    address: propertyDetail.address,
                    addressUnverified: propertyDetail.address_unverified,
                    regions: propertyDetail.regions,
                    latitude: propertyDetail.latitude,
                    longitude: propertyDetail.longitude,
                    listings: propertyDetail.listings,
                  }
                : null
            }
            licenses={propertyDetail?.licenses ?? []}
            actions={
              propertyDetailId ? (
                <CatalogRowLinks
                  propertyListingId={propertyDetailId}
                  returnTo={returnTo}
                  layout="row"
                />
              ) : null
            }
            onOpenLicense={(licenseId) => {
              const scoped = scopedStrLicenseId(
                licenseId,
                propertyDetail?.licenses ?? [],
              );
              if (scoped) setPropertyLicenseId(scoped);
            }}
          />
          <StrLinkedLicenseSheet
            licenseId={propertyLicenseId}
            onLicenseIdChange={setPropertyLicenseId}
            userType={UserType.OPERATOR}
          />
        </>
      )}
    </>
  );
}

function CatalogRowLinks({
  listingId,
  propertyListingId,
  returnTo,
  layout = "column",
}: {
  listingId?: string;
  propertyListingId?: string;
  returnTo: string;
  layout?: "column" | "row";
}) {
  const duplicateListingId = listingId ?? propertyListingId;
  return (
    <div
      className={
        layout === "row" ? "flex flex-wrap gap-2" : "flex flex-col gap-1"
      }
    >
      <Button asChild size="sm" variant="outline">
        <Link
          to="/operator/str/address-mapping"
          search={{
            listingId,
            propertyListingId,
            returnTo,
          }}
          onClick={(event) => event.stopPropagation()}
        >
          Address mapping
        </Link>
      </Button>
      <Button asChild size="sm" variant="outline">
        <Link
          to="/operator/str/deduplication"
          search={{
            listingId: duplicateListingId,
            returnTo,
          }}
          onClick={(event) => event.stopPropagation()}
        >
          Mark Duplicate
        </Link>
      </Button>
    </div>
  );
}

export function StrCatalogFrame<T extends object, K extends string>({
  chrome,
  isMapView,
  onListView,
  pins,
  infoCard,
  pinsLoading,
  pinsError,
  onPinsRetry,
  onPinClick,
  regionOverlay,
  columnFilterBadges,
  filters,
  onFiltersChange,
  accessorKeys,
  getColumnProperties,
  tablePersistenceKey,
  rows,
  getRowId,
  onRowClick,
  renderRowActions,
  ...tableProps
}: {
  chrome: ReactNode;
  isMapView: boolean;
  onListView: () => void;
  pins: DashboardMapPin[];
  infoCard: DashboardMapInfoCard;
  pinsLoading: boolean;
  pinsError: unknown;
  onPinsRetry: () => void;
  onPinClick: (id: string) => void;
  regionOverlay?: {
    selectedJurisdictionIds: readonly string[];
    portalJurisdictionIds: readonly string[];
    onRegionFilterChange: (regionIds: string[]) => void;
  };
  columnFilterBadges: ReturnType<typeof buildColumnFilterBadges>;
  filters: Record<string, string[]>;
  onFiltersChange: (filters: Record<string, string[]>) => void;
  accessorKeys: readonly K[];
  getColumnProperties: (key: K) => ColumnProperties<T, K>;
  tablePersistenceKey: string;
  rows: T[];
  getRowId: (row: T) => string;
  onRowClick: (id: string) => void;
  renderRowActions?: (row: T) => ReactNode;
} & Omit<
  ComponentProps<typeof DashboardTable<T, K>>,
  | "data"
  | "accessorKeys"
  | "getColumnProperties"
  | "getRowId"
  | "onRowClick"
  | "renderRowActions"
  | "leadingContent"
  | "tablePersistenceKey"
>) {
  if (isMapView) {
    return (
      <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
        <DashboardMapViewLayout
          leadingContent={chrome}
          facets={
            <DashboardMapFacetRail
              data={rows}
              accessorKeys={accessorKeys}
              getColumnProperties={getColumnProperties}
              columnFilters={filters}
              onColumnFiltersChange={onFiltersChange}
              onFilterValuesRequest={tableProps.onFilterPopoverOpen}
              tablePersistenceKey={tablePersistenceKey}
            />
          }
          filterBadges={
            <DashboardColumnFilterBadges
              badges={columnFilterBadges}
              onClearAll={() => onFiltersChange({})}
              onRemove={(badge) =>
                onFiltersChange(removeColumnFilterBadge(filters, badge))
              }
            />
          }
        >
          {pinsError ? (
            <ErrorComponent
              title={getQueryErrorHeading(pinsError)}
              message={getQueryErrorMessage(pinsError)}
              action={{ label: "Retry", onClick: onPinsRetry }}
            />
          ) : (
            <MapErrorBoundary
              onSwitchToListView={onListView}
              className="h-full w-full"
            >
              <DashboardMap
                className="h-full min-h-0 w-full"
                pins={pins}
                infoCard={infoCard}
                isLoading={pinsLoading}
                onPinClick={onPinClick}
                regionOverlay={regionOverlay}
                showRegistrationStatusFlowInfo={false}
              />
            </MapErrorBoundary>
          )}
        </DashboardMapViewLayout>
      </div>
    );
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
      <DashboardTable
        leadingContent={chrome}
        data={rows}
        accessorKeys={accessorKeys}
        getColumnProperties={getColumnProperties}
        getRowId={getRowId}
        onRowClick={onRowClick}
        renderRowActions={renderRowActions}
        tablePersistenceKey={tablePersistenceKey}
        {...tableProps}
      />
    </div>
  );
}
